import { ChangeEvent, useMemo, useRef, useState } from 'react'
import { isEmpty } from 'ramda'

import {
  useDesktopReportsInOrg,
  useDesktopReportsInOrgMutate,
} from 'app/dataProviders/reports/desktop/hooks'

import {
  PageTemplateContent,
  Header2,
  Loading,
  StatusMessage,
} from 'app/common/templates-next/page-template'

import { OrgId } from 'app/common/types/id'
import Prompt from 'app/components/common/Prompt'
import SaveCancelButtons from 'app/components/common/SaveCancelButtons'
import { DesktopReportInOrgShortInfo, KnownDesktopReport } from 'app/common/types/reports/desktop'
import PlainCheckbox from 'app/common/ui-next/plain-checkbox'
import ControlsBar from 'app/common/ui-next/controls-bar'
import { useWriteAccess } from 'app/rights/RightsForOrg'

import MultipleSubmitSettings from '../MultipleSubmitSettings'
import MultipleModalAdditionalContent from './MultipleModalAdditionalContent'
import CopySettingsContainer from '../common/CopySettingsContainer'
import { translate } from './translate'

import styles from './Reports.module.scss'
import CopySettings from '../CopySettings'
import { rightsPath } from '../rightsPath'

const reportArrange = [
  KnownDesktopReport.DailySales,
  KnownDesktopReport.PaymentModeSales,
  KnownDesktopReport.FundsSales,
  KnownDesktopReport.DailyOrders,
  KnownDesktopReport.DailyDistribution,
  KnownDesktopReport.DayBySaleType,
  KnownDesktopReport.DayCash
]

type Props = {
  orgId: OrgId
}

export const Reports = ({ orgId }: Props) => {
  const formRef = useRef<HTMLFormElement | null>(null)

  const writeAccess = useWriteAccess(rightsPath)

  const { data, loading, requestRef, setData } = useDesktopReportsInOrg({ params: { orgId } })
  const [submitting, setSubmitting] = useState(false)
  const [userInput, setUserInput] = useState<Record<string, DesktopReportInOrgShortInfo> | {}>({})
  const modified = !isEmpty(userInput)

  const reportSettingsCurrent = Array.from(data.entries()).reduce(
    (acc, [key, value]) => {
      acc[key] = userInput[key] ? { ...value, ...userInput[key] } : value
      return acc
    },
    {} as Record<string, DesktopReportInOrgShortInfo>,
  )

  const { requestRef: submitRef } = useDesktopReportsInOrgMutate({
    params: { reportsSettings: reportSettingsCurrent },
  })

  const handleSave = () => {
    if (formRef.current) {
      ; (formRef.current.submit as any).click()
    }
  }
  const submit = async (orgs?: Array<number>) => {
    if (!orgs) return false
    setSubmitting(true)
    const res = await submitRef.current({ orgIds: orgs.map((id) => String(id) as OrgId) })
    setSubmitting(false)
    return !!res
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    if (/^restrict_print_/.test(value)) {
      const realValue = value.replace(/^restrict_print_/, '')
      setUserInput((prev) => ({
        ...prev,
        [realValue]: {
          ...prev[realValue],
          restrictPrint: checked,
        },
      }))
      return
    }
    setUserInput((prev) => ({
      ...prev,
      [value]: {
        ...prev[value],
        schoolEnabled: checked,
      },
    }))
  }

  const reset = () => {
    setUserInput({})
    requestRef.current()
  }

  const handleSaveSuccess = () => {
    if (!isEmpty(userInput)) {
      setData((prev) => {
        const updated = new Map(prev)
        Object.entries(userInput).forEach(([id, changes]) => {
          const prevItem = prev.get(id)
          if (prevItem) {
            updated.set(id, { ...prevItem, ...changes })
          }
        })
        return updated
      })
    }
    setUserInput({})
  }

  const reportsAvailable = useMemo(() => {
    return reportArrange.reduce(
      (acc, id) => {
        const item = data.get(id)
        if (!item) return acc
        const { supplierEnabled, supplierUse } = item
        if (supplierEnabled && supplierUse) {
          acc.push({ id, title: translate(id) })
        }
        return acc
      },
      [] as Array<{ id: string; title: string }>,
    )
  }, [data])


  return (
    <>
      <PageTemplateContent>
        <ControlsBar spaceBetween>
          <Header2 skipMargin>{'Отчеты'}</Header2>
          {modified && (
            <SaveCancelButtons
              onSave={handleSave}
              onCancel={reset}
              disabled={loading || submitting}
              saving={submitting}
              cancelling={modified && loading}
            />
          )}
        </ControlsBar>
      </PageTemplateContent>

      <PageTemplateContent className={styles.content}>
        {loading && <Loading />}
        {!loading && (
          <MultipleSubmitSettings
            additionalContent={<MultipleModalAdditionalContent />}
            onSubmitArray={submit}
            onChangeSubmitState={setSubmitting}
            onSuccess={handleSaveSuccess}
            formId="use_reports_form"
          >
            {(toggleMultipleSubmitModal, multipleSubmit) => (
              <form onSubmit={multipleSubmit} ref={formRef} id="use_reports_form">
                {reportsAvailable.length === 0 && (
                  <StatusMessage skipPadding>{'Отсутствуют доступные отчеты'}</StatusMessage>
                )}
                <CopySettingsContainer>
                  <div>
                    {reportsAvailable.length > 0 && (
                      <div className={styles.preText}>
                        {
                          'Выберите необходимые отчеты для настольного приложения «Инновационная школа — Клиент»'
                        }
                      </div>
                    )}

                    {reportsAvailable.map(({ id, title }) => {
                      const hasRestrictPrint = 'restrictPrint' in reportSettingsCurrent[id]

                      return (
                        <div key={id} className={styles.item}>
                          <PlainCheckbox
                            value={id}
                            label={title}
                            disabled={loading || submitting}
                            checked={reportSettingsCurrent[id]?.schoolEnabled}
                            onChange={handleChange}
                          />
                          {hasRestrictPrint && (
                            <PlainCheckbox
                              value={`restrict_print_${id}`}
                              level={1}
                              className={styles.itemCheckboxSecond}
                              label={translate('restrict_print')}
                              disabled={loading || submitting}
                              checked={reportSettingsCurrent[id]?.restrictPrint}
                              onChange={handleChange}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {writeAccess && <CopySettings onSubmit={submit} />}
                </CopySettingsContainer>

                <button type="submit" name="submit" className={styles.hidden}>
                  {'Сохранить'}
                </button>
              </form>
            )}
          </MultipleSubmitSettings>
        )}

        <Prompt when={modified} message="Вы не сохранили изменения, перейти в любом случае?" />
      </PageTemplateContent>
    </>
  )
}
